/**
 * Itinerary View Component
 * Displays the generated itinerary with tours and transfers
 */

import React, { useState } from 'react';
import { MapPinIcon, ClockIcon, UsersIcon, ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { CustomSafariItinerary } from '../../types/customSafari';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { customSafariService } from '../../services/customSafariService';
import SafariRouteMap from '../Maps/SafariRouteMap';
import { optimizeItinerary } from '../../utils/routeOptimizer';
import { ItineraryItem } from '../../types';

interface ItineraryViewProps {
  itinerary: CustomSafariItinerary;
  onItineraryUpdate: (itinerary: CustomSafariItinerary) => void;
  onLaunchDemo?: () => void;
}

const ItineraryView: React.FC<ItineraryViewProps> = ({
  itinerary,
  onItineraryUpdate,
  onLaunchDemo
}) => {
  const { actualTheme } = useTheme();
  const { showSuccess, showError } = useToast();
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [isCreatingDemo, setIsCreatingDemo] = useState(false);

  // Convert SelectedTours to ItineraryItems for Map & Optimization
  const mapItems: ItineraryItem[] = itinerary.tours.map((t, index) => ({
    day: index + 1,
    title: t.tour.title,
    description: '',
    activities: [],
    meals: [],
    location: t.tour.location
  }));

  const handleSimulationStart = () => {
    setIsSimulating(true);
  };

  const handleOptimizeRoute = async () => {
    setIsOptimizing(true);
    try {
      // 1. Create optimization-compatible items with original indices
      // We assume the first tour is FIXED (Arrival) and last is FIXED (Departure) if > 2 items
      const itemsToOptimize = itinerary.tours.map((t, index) => ({
        ...mapItems[index],
        originalId: t.tour.id
      }));

      // 2. Run Greedy Nearest Neighbor Algorithm with Fixed Start/End
      const optimizedItems = optimizeItinerary(
        itemsToOptimize,
        itemsToOptimize[0].location, // Start
        itemsToOptimize[itemsToOptimize.length - 1].location // End
      ) as (ItineraryItem & { originalId: string })[];

      // 3. Extract new order of IDs
      const newTourIds = updatedTourIds(optimizedItems);

      // 4. Update Itinerary
      const updated = await customSafariService.reorderTours(itinerary, newTourIds);
      onItineraryUpdate(updated);
      showSuccess('Route Optimized', 'Your tours have been reorganized to minimize travel distance.');
    } catch (error) {
      console.error("Optimization failed:", error);
      showError('Optimization Failed', 'We couldn\'t optimize the route. Please try again.');
    } finally {
      setIsOptimizing(false);
    }
  };

  const updatedTourIds = (items: (ItineraryItem & { originalId: string })[]) => {
    return items.map(item => item.originalId);
  }

  // Demo: Create a 7-Day Kenya Safari Simulation
  const handleCreateDemoItinerary = async () => {
    if (onLaunchDemo) {
      onLaunchDemo();
      return;
    }

    setIsCreatingDemo(true);
    try {
      // Realistic 7-Day Kenya Route Mock Tours
      const demoLocations = [
        { name: 'JKIA Airport (Arrival)', coordinates: { lat: -1.332, lng: 36.927 } },
        { name: 'Lake Nakuru National Park', coordinates: { lat: -0.367, lng: 36.083 } },
        { name: 'Masai Mara Reserve', coordinates: { lat: -1.483, lng: 35.143 } },
        { name: 'Amboseli National Park', coordinates: { lat: -2.650, lng: 37.250 } },
        { name: 'Tsavo West National Park', coordinates: { lat: -2.983, lng: 38.167 } },
        { name: 'JKIA Airport (Departure)', coordinates: { lat: -1.332, lng: 36.927 } }
      ];

      const mockTours = demoLocations.map((loc, i) => ({
        tour: {
          id: `demo_tour_${i}`,
          title: i === 0 ? 'International Arrival & Transfer' : i === 5 ? 'Final Transfer & Departure' : `Safari Day ${i + 1}: ${loc.name}`,
          duration: '1 day',
          price: 250,
          currency: 'USD',
          location: {
            name: loc.name,
            country: 'Kenya',
            region: 'E. Africa',
            coordinates: loc.coordinates,
            address: ''
          },
          maxParticipants: 12,
          active: true,
          images: [],
          description: `A beautiful day at ${loc.name}.`,
          highlights: [],
          itinerary: [],
          includes: [],
          excludes: [],
          requirements: [],
          difficulty: 'Moderate',
          rating: 5,
          reviewCount: 100,
          category: 'Safari',
          guide: { id: 'demo_guide', firstName: 'John', lastName: 'Doe', rating: 5, specialties: ['Wildlife'], languages: ['English'], bio: 'Pro guide' },
          availability: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as any, // Cast as any to simplify for demo
        order: i + 1,
        participants: itinerary.tours[0]?.participants || 2
      }));

      const updated = await customSafariService.generateItinerary(
        mockTours.map(t => t.tour),
        "7-Day Dream Kenya Safari",
        itinerary.totalPrice.currency
      );
      onItineraryUpdate(updated);
      showSuccess('Demo Created', 'Enjoy exploring this 7-day Kenya adventure!');
    } catch (error) {
      console.error("Demo creation failed:", error);
      showError('Demo Failed', 'Failed to generate demo itinerary.');
    } finally {
      setIsCreatingDemo(false);
    }
  };


  const handleReorder = async (fromIndex: number, toIndex: number) => {
    const newOrder = [...itinerary.tours];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);

    const tourIds = newOrder.map(t => t.tour.id);
    const updated = await customSafariService.reorderTours(itinerary, tourIds);
    onItineraryUpdate(updated);
  };

  const handleParticipantsChange = async (tourId: string, participants: number) => {
    // Ensure participants is at least 1
    const validParticipants = Math.max(1, participants);
    const updated = await customSafariService.updateParticipants(itinerary, tourId, validParticipants);
    onItineraryUpdate(updated);
  };

  return (
    <div className={`rounded-lg ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white border border-neutral-200'
      } p-6`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h3 className={`text-xl font-bold ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
            }`}>
            Your Safari Itinerary
          </h3>
          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
            }`}>
            {itinerary.totalDuration} day{itinerary.totalDuration !== 1 ? 's' : ''} total
          </div>
        </div>

        <div className="flex gap-2">
          {itinerary.tours.length > 2 && (
            <button
              onClick={handleOptimizeRoute}
              disabled={isOptimizing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${actualTheme === 'dark'
                ? 'bg-primary-700 text-white hover:bg-primary-600'
                : 'bg-primary-100 text-primary-800 hover:bg-primary-200'
                }`}
            >
              <SparklesIcon className={`h-4 w-4 ${isOptimizing ? 'animate-spin' : ''}`} />
              {isOptimizing ? 'Optimizing...' : 'Optimize Order'}
            </button>
          )}
          <button
            onClick={handleCreateDemoItinerary}
            disabled={isCreatingDemo}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${actualTheme === 'dark'
              ? 'bg-blue-700 text-white hover:bg-blue-600'
              : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
              }`}
          >
            <SparklesIcon className={`h-4 w-4 ${isCreatingDemo ? 'animate-spin' : ''}`} />
            {isCreatingDemo ? 'Creating...' : '7-Day Demo'}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {itinerary.tours.map((selectedTour, index) => {
          const transfer = itinerary.transfers.find(
            t => t.from.tourId === selectedTour.tour.id
          );

          return (
            <div key={selectedTour.tour.id}>
              {/* Tour Card */}
              <div className={`rounded-lg p-5 ${actualTheme === 'dark' ? 'bg-slate-700' : 'bg-primary-50'
                }`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold ${actualTheme === 'dark'
                      ? 'bg-primary-600 text-white'
                      : 'bg-primary-800 text-white'
                      }`}>
                      {selectedTour.order}
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-lg font-semibold mb-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                        }`}>
                        {selectedTour.tour.title}
                      </h4>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className={`flex items-center gap-1 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                          }`}>
                          <MapPinIcon className="h-4 w-4" />
                          {selectedTour.tour.location.name}
                        </div>
                        <div className={`flex items-center gap-1 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                          }`}>
                          <ClockIcon className="h-4 w-4" />
                          {selectedTour.tour.duration}
                        </div>
                        <div className={`flex items-center gap-1 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                          }`}>
                          <UsersIcon className="h-4 w-4" />
                          {selectedTour.participants} participant{selectedTour.participants !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`text-right ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                    }`}>
                    <div className="text-lg font-bold">
                      {selectedTour.tour.currency} {(selectedTour.tour.price * selectedTour.participants).toLocaleString()}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      per person: {selectedTour.tour.currency} {selectedTour.tour.price.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Participants Selector */}
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-neutral-200 dark:border-slate-600">
                  <label className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Participants:
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        if (selectedTour.participants > 1) {
                          handleParticipantsChange(selectedTour.tour.id, selectedTour.participants - 1);
                        }
                      }}
                      disabled={selectedTour.participants <= 1}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all ${selectedTour.participants <= 1
                        ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                        : actualTheme === 'dark'
                          ? 'bg-slate-600 hover:bg-slate-500 text-white'
                          : 'bg-white border border-neutral-300 hover:bg-primary-50 text-primary-800'
                        }`}
                    >
                      -
                    </button>
                    <span className={`w-12 text-center font-semibold ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                      }`}>
                      {selectedTour.participants}
                    </span>
                    <button
                      onClick={() => {
                        if (selectedTour.participants < selectedTour.tour.maxParticipants) {
                          handleParticipantsChange(selectedTour.tour.id, selectedTour.participants + 1);
                        }
                      }}
                      disabled={selectedTour.participants >= selectedTour.tour.maxParticipants}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all ${selectedTour.participants >= selectedTour.tour.maxParticipants
                        ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                        : actualTheme === 'dark'
                          ? 'bg-slate-600 hover:bg-slate-500 text-white'
                          : 'bg-white border border-neutral-300 hover:bg-primary-50 text-primary-800'
                        }`}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Transfer Card */}
              {transfer && index < itinerary.tours.length - 1 && (
                <div className="my-4 flex items-center justify-center">
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${actualTheme === 'dark' ? 'bg-slate-700' : 'bg-neutral-100'
                    }`}>
                    <ArrowRightIcon className={`h-5 w-5 ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                      }`} />
                    <div className="text-sm">
                      <div className={`font-medium ${actualTheme === 'dark' ? 'text-white' : 'text-neutral-900'
                        }`}>
                        {transfer.transferType === 'flight' ? '✈️ Flight' : '🚗 Road'} Transfer
                      </div>
                      <div className={`text-xs ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                        }`}>
                        {transfer.distance}km • {transfer.estimatedTime} hours • {transfer.currency} {transfer.price.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Route Visualization Window (Bottom) */}
      <div className={`mt-8 pt-8 border-t ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
        }`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className={`text-lg font-bold flex items-center gap-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
            }`}>
            <MapPinIcon className="h-5 w-5" />
            Route Preview
          </h4>
          <button
            onClick={handleSimulationStart}
            disabled={isSimulating}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm font-bold rounded-full shadow-lg hover:bg-blue-500 transition-all flex items-center gap-2"
          >
            {isSimulating ? 'Driving...' : '▶ Start Preview Drive'}
          </button>
        </div>
        <SafariRouteMap
          items={mapItems}
          isSimulating={isSimulating}
          onSimulationComplete={() => setIsSimulating(false)}
        />
      </div>
    </div>
  );
};

export default ItineraryView;

